import { useState, useMemo, useRef, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useFormContext, useFieldArray, useFormState } from "react-hook-form";
import { Search, Info } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { usePage } from "@inertiajs/react";
import {
  GeoTargetConstantsAPIService,
  type SearchGeoTargetConstantsResponse,
} from "@rails_api_base";
import type { CampaignProps } from "@components/ads/workflow-panel/workflow-buddy/ad-campaign.types";
import type { SettingsFormData, LocationWithSettings } from "./settingsForm.schema";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@components/ui/input-group";
import LocationTargetingItem from "./LocationTargetingItem";
import { useSettingsFormStore } from "@stores/settingsFormStore";
import { defaultLocationTarget } from "./defaultLocationTarget";

type GeoTarget = NonNullable<SearchGeoTargetConstantsResponse>[number];

export default function LocationTargeting() {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearchValue(value);
  }, 300);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { control } = useFormContext<SettingsFormData>();
  const { errors } = useFormState({ control });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "locations",
  });

  const { jwt, default_country } = usePage<CampaignProps>().props;
  const service = useMemo(() => new GeoTargetConstantsAPIService({ jwt }), [jwt]);

  // The default country target we seed and auto-manage tracks the account's
  // market (US or Canada). It's the one we drop when a specific location is
  // picked and restore when the last specific location is removed.
  const defaultTarget = useMemo(() => defaultLocationTarget(default_country), [default_country]);
  const DEFAULT_CRITERIA_ID = defaultTarget.criteria_id;

  const { data: suggestions = [], isLoading } = useQuery({
    queryKey: ["geoTargetConstants", debouncedSearchValue],
    queryFn: () => service.search({ location_query: debouncedSearchValue }),
    enabled: debouncedSearchValue.length >= 2,
    staleTime: 1000 * 60 * 5,
  });

  const filteredSuggestions = suggestions.filter(
    (s) => !fields.some((loc) => loc.criteria_id === s.criteria_id)
  );

  const hasError = errors.locations?.message;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSetSearch(value);
    setIsDropdownOpen(true);
  };

  const handleSelectLocation = (location: GeoTarget) => {
    if (!location) {
      return;
    }

    const newLocation: LocationWithSettings = {
      criteria_id: location.criteria_id,
      name: location.name,
      canonical_name: location.canonical_name,
      target_type: location.target_type,
      country_code: location.country_code!,
      isTargeted: true,
    };
    append(newLocation);

    // Adding any specific location removes the account's country default
    // (the one we manage, so it is always the one dropped — incl. when the
    // specific location is in another country).
    if (location.criteria_id !== DEFAULT_CRITERIA_ID) {
      const defaultIndex = fields.findIndex((f) => f.criteria_id === DEFAULT_CRITERIA_ID);
      if (defaultIndex !== -1) {
        remove(defaultIndex);
      }
    }

    setSearchValue("");
    debouncedSetSearch.cancel();
    setDebouncedSearchValue("");
    setIsDropdownOpen(false);
  };

  // Track when we need to restore the country default after removing all
  // specific locations.
  const pendingRestoreDefault = useRef(false);

  const handleRemoveLocation = (index: number) => {
    const removedLocation = fields[index];

    // If we just removed a specific location, restore the country default
    // once no specific locations of any country remain.
    if (removedLocation.criteria_id !== DEFAULT_CRITERIA_ID) {
      // Check if there are any other specific locations remaining (excluding the one being removed)
      const remainingSpecificLocations = fields.filter(
        (f, i) => i !== index && f.criteria_id !== DEFAULT_CRITERIA_ID
      );

      // If no more specific locations remain, mark for restoring the default
      if (remainingSpecificLocations.length === 0) {
        // Check if the country default is not already in the list
        const defaultExists = fields.some((f) => f.criteria_id === DEFAULT_CRITERIA_ID);
        if (!defaultExists) {
          pendingRestoreDefault.current = true;
        }
      }
    }

    remove(index);
  };

  // Effect to restore the country default after fields array is updated from remove
  useEffect(() => {
    if (pendingRestoreDefault.current) {
      pendingRestoreDefault.current = false;
      // Add the country default back to the list as targeted
      const defaultLocation: LocationWithSettings = {
        criteria_id: defaultTarget.criteria_id,
        name: defaultTarget.name,
        canonical_name: defaultTarget.canonical_name,
        target_type: defaultTarget.target_type,
        country_code: defaultTarget.country_code ?? "US",
        isTargeted: true,
      };
      append(defaultLocation);
    }
  }, [fields, append]);

  const handleToggleTargeted = (index: number) => {
    const current = fields[index];
    update(index, { ...current, isTargeted: !current.isTargeted });
  };

  const hasInitialized = useRef(false);
  const { hasHydrated } = useSettingsFormStore();

  // Seed the country default ONLY for a genuinely empty (fresh) campaign, and
  // only once. We latch `hasInitialized` the moment we've hydrated — even when
  // the campaign already had locations — so this never re-fires later. Without
  // that latch, deleting the last specific location drops fields to 0 and this
  // effect would seed a second default on top of the restore effect's one.
  useEffect(() => {
    if (hasHydrated && !hasInitialized.current) {
      hasInitialized.current = true;
      if (fields.length === 0) {
        handleSelectLocation(defaultTarget);
      }
    }
  }, [hasHydrated]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <label className="text-sm font-semibold leading-[18px] text-base-500">
            Location Targeting
          </label>
          {hasError && <span className="text-xs leading-4 text-[#d14f34]">{hasError}</span>}
        </div>
        <div className="relative">
          <InputGroup className="rounded-full">
            <InputGroupInput
              ref={inputRef}
              type="text"
              placeholder="Search for cities, counties, states or countries..."
              value={searchValue}
              onChange={handleSearchChange}
              onFocus={() => setIsDropdownOpen(true)}
              data-testid="location-search-input"
            />
            <InputGroupAddon align="inline-end">
              <Search className="size-3.5 text-base-600" />
            </InputGroupAddon>
          </InputGroup>
          {isDropdownOpen && searchValue.length >= 2 && (
            <div
              ref={dropdownRef}
              className="absolute z-10 mt-1 w-full rounded-lg border border-neutral-200 bg-white shadow-lg max-h-60 overflow-auto"
            >
              {isLoading ? (
                <div className="p-3 text-sm text-neutral-500">Searching...</div>
              ) : filteredSuggestions.length === 0 ? (
                <div className="p-3 text-sm text-neutral-500">No locations found</div>
              ) : (
                filteredSuggestions.map((location) => (
                  <button
                    key={location.criteria_id}
                    type="button"
                    className="w-full px-4 py-2 text-left hover:bg-neutral-50 flex items-center justify-between"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectLocation(location);
                    }}
                  >
                    <span className="text-sm">{location.canonical_name}</span>
                    <span className="text-xs text-base-300 bg-neutral-100 px-2 py-0.5 rounded">
                      {location.target_type}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 rounded-lg border border-[#5f7e78] bg-[#eaf5f3] p-4 max-w-[580px]">
          <Info className="h-4 w-4 text-[#0d342b] flex-shrink-0" />
          <p className="text-sm leading-[18px] text-[#081f1a]">
            For small businesses, a narrow local geographic focus is the best starting point.
            {/* TODO: This copy should smart update dependent on the what we have collected about their business so far */}
          </p>
        </div>

        {fields.length > 0 && (
          <div className="flex flex-col gap-2 mt-2">
            {fields.map((location, index) => (
              <LocationTargetingItem
                key={location.id}
                location={location}
                index={index}
                handleRemoveLocation={handleRemoveLocation}
                handleToggleTargeted={handleToggleTargeted}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
